/**
 * Portal authentication store.
 *
 * Manages JWT token storage/retrieval and portal user state.
 * Token is stored in httpOnly cookie (set by server) and sent automatically by browser.
 * Supports two login flows:
 *  - Username/password for property managers
 *  - SMS OTP for unit owners / residents
 */

import { create } from 'zustand';

function getCookieValue(cookieHeader: string | null, key: string): string | null {
  if (cookieHeader === null || cookieHeader === '') {
    return null;
  }
  const cookies = cookieHeader.split(';').map((c) => c.trim());
  const tokenCookie = cookies.find((c) => c.startsWith(`${key}=`));
  return tokenCookie !== undefined ? (tokenCookie.split('=')[1] ?? null) : null;
}

export interface PortalUser {
  username: string;
  clientCode: string;
  clientName: string;
  portalRole: 'property_manager' | 'unit_owner';
  contactId: number | null;
}

interface LoginResponse {
  user: PortalUser;
}

interface PortalAuthState {
  user: PortalUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  sendCode: (phone: string) => Promise<boolean>;
  verifyCode: (phone: string, code: string) => Promise<boolean>;
  logout: () => void;
  verify: () => Promise<void>;
}

const initialToken =
  typeof document !== 'undefined'
    ? getCookieValue(document.cookie, 'merritrac-portal-token')
    : null;

export const usePortalAuth = create<PortalAuthState>((set) => ({
  user: null,
  token: initialToken,
  isAuthenticated: false,
  isLoading: initialToken !== null,
  error: null,

  login: async (username: string, password: string): Promise<boolean> => {
    set({ isLoading: true, error: null });

    try {
      const res = await fetch('/portal/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const body = (await res.json()) as { message?: string };
        set({
          isLoading: false,
          error: body.message ?? 'Login failed',
          isAuthenticated: false,
        });
        return false;
      }

      const data = (await res.json()) as LoginResponse;

      set({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return true;
    } catch {
      set({ isLoading: false, error: 'Network error', isAuthenticated: false });
      return false;
    }
  },

  sendCode: async (phone: string): Promise<boolean> => {
    set({ error: null });

    try {
      const res = await fetch('/portal/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone }),
      });

      if (!res.ok) {
        const body = (await res.json()) as { message?: string };
        set({ error: body.message ?? 'Failed to send code' });
        return false;
      }

      return true;
    } catch {
      set({ error: 'Network error' });
      return false;
    }
  },

  verifyCode: async (phone: string, code: string): Promise<boolean> => {
    set({ isLoading: true, error: null });

    try {
      const res = await fetch('/portal/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone, code }),
      });

      if (!res.ok) {
        const body = (await res.json()) as { message?: string };
        set({
          isLoading: false,
          error: body.message ?? 'Invalid code',
          isAuthenticated: false,
        });
        return false;
      }

      const data = (await res.json()) as LoginResponse;

      set({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return true;
    } catch {
      set({ isLoading: false, error: 'Network error', isAuthenticated: false });
      return false;
    }
  },

  logout: (): void => {
    document.cookie = 'merritrac-portal-token=; Max-Age=0; path=/';
    set({ user: null, token: null, isAuthenticated: false, error: null });
  },

  verify: async (): Promise<void> => {
    set({ isLoading: true });

    try {
      const res = await fetch('/portal/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!res.ok) {
        document.cookie = 'merritrac-portal-token=; Max-Age=0; path=/';
        set({ user: null, token: null, isAuthenticated: false, isLoading: false });
        return;
      }

      const data = (await res.json()) as { user: PortalUser };
      set({ user: data.user, isAuthenticated: true, isLoading: false });
    } catch {
      // Network error - don't clear the cookie, user can retry
      set({ user: null, token: null, isLoading: false, isAuthenticated: false });
    }
  },
}));
